/**
 * MiniModPanel - Interface du synthé modulaire complet avec câbles SVG
 *
 * Modules:
 * - VCO 1/2/3 avec FM inputs
 * - LFO 1/2
 * - NOISE
 * - ENV 1/2 avec trigger
 * - VCF avec mod input
 * - VCA 1/2 avec mod inputs
 * - MIX 4 canaux
 * - MULT (splitter)
 * - RING (ring modulator)
 * - S&H (sample & hold)
 * - SLEW (slew limiter)
 * - OUTPUT
 *
 * Utilise le séquenceur principal 16 steps
 */

import { themeManager } from '../themes/ThemeManager.js';
import { WaveSelector } from '../components/WaveSelector.js';
import { MINIMOD_PRESETS } from '../../audio/MiniMod.js';

export class MiniModPanel {
    constructor(minimod, store, sequencer) {
        this.minimod = minimod;
        this.store = store;
        this.sequencer = sequencer;
        this.panelId = 'minimod';

        // État du drag pour les câbles
        this.dragging = null;
        this.tempCable = null;

        // Positions des jacks
        this.jackPositions = {};

        // Composants UI
        this.components = { knobs: {}, waveSelectors: {}, selects: {}, stepButtons: [] };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('minimod');

        this.minimod.setPower(isPowered);

        container.innerHTML = `
            <div class="machine minimod ${isCollapsed ? 'collapsed' : ''} ${!isPowered ? 'powered-off' : ''}" id="minimod_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="minimod_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    MiniMod <span class="brand">Modular Synth</span>
                    <select class="minimod-preset-select" id="minimod_preset">
                        ${Object.keys(MINIMOD_PRESETS).map(name =>
                            `<option value="${name}">${name}</option>`
                        ).join('')}
                    </select>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="minimod_power" title="Power ON/OFF">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="minimod_content">
                    <div class="minimod-container">
                        <!-- SVG Layer pour les câbles -->
                        <svg class="minimod-cables" id="minimod_cables">
                            <defs>
                                <linearGradient id="cableGradientAudio" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" style="stop-color:#ffcc00"/>
                                    <stop offset="100%" style="stop-color:#ff9900"/>
                                </linearGradient>
                                <linearGradient id="cableGradientMod" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" style="stop-color:#00ccff"/>
                                    <stop offset="100%" style="stop-color:#0099ff"/>
                                </linearGradient>
                                <linearGradient id="cableGradientGate" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" style="stop-color:#ff00ff"/>
                                    <stop offset="100%" style="stop-color:#cc00cc"/>
                                </linearGradient>
                                <filter id="cableGlow">
                                    <feGaussianBlur stdDeviation="2" result="blur"/>
                                    <feMerge>
                                        <feMergeNode in="blur"/>
                                        <feMergeNode in="SourceGraphic"/>
                                    </feMerge>
                                </filter>
                            </defs>
                            <g id="minimod_cableGroup"></g>
                            <path id="minimod_tempCable" class="temp-cable" d="" fill="none" stroke="#fff" stroke-width="3" stroke-dasharray="5,5"/>
                        </svg>

                        <!-- Row 1: Oscillators -->
                        <div class="minimod-row">
                            <!-- VCO 1 -->
                            <div class="mod-module mod-vco1">
                                <div class="mod-title">VCO 1</div>
                                <div class="mod-controls" id="minimod_vco1_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack output" data-jack="vco1Out" title="VCO1 Audio Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="vco1Fm" title="Frequency Modulation">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">FM</span>
                                    </div>
                                </div>
                            </div>

                            <!-- VCO 2 -->
                            <div class="mod-module mod-vco2">
                                <div class="mod-title">VCO 2</div>
                                <div class="mod-controls" id="minimod_vco2_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack output" data-jack="vco2Out" title="VCO2 Audio Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="vco2Fm" title="Frequency Modulation">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">FM</span>
                                    </div>
                                </div>
                            </div>

                            <!-- VCO 3 -->
                            <div class="mod-module mod-vco3">
                                <div class="mod-title">VCO 3</div>
                                <div class="mod-controls" id="minimod_vco3_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack output" data-jack="vco3Out" title="VCO3 Audio Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="vco3Fm" title="Frequency Modulation">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">FM</span>
                                    </div>
                                </div>
                            </div>

                            <!-- NOISE -->
                            <div class="mod-module mod-noise">
                                <div class="mod-title">NOISE</div>
                                <div class="mod-jacks">
                                    <div class="mod-jack output" data-jack="noiseOut" title="White Noise">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Row 2: LFOs, ENVs -->
                        <div class="minimod-row">
                            <!-- LFO 1 -->
                            <div class="mod-module mod-lfo1">
                                <div class="mod-title">LFO 1</div>
                                <div class="mod-controls" id="minimod_lfo1_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack output mod" data-jack="lfo1Out" title="LFO1 Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>

                            <!-- LFO 2 -->
                            <div class="mod-module mod-lfo2">
                                <div class="mod-title">LFO 2</div>
                                <div class="mod-controls" id="minimod_lfo2_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack output mod" data-jack="lfo2Out" title="LFO2 Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>

                            <!-- ENV 1 -->
                            <div class="mod-module mod-env1">
                                <div class="mod-title">ENV 1</div>
                                <div class="mod-controls" id="minimod_env1_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack output mod" data-jack="env1Out" title="Envelope 1">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                                <button class="mod-trigger-btn" id="minimod_env1_trigger">TRIG</button>
                            </div>

                            <!-- ENV 2 -->
                            <div class="mod-module mod-env2">
                                <div class="mod-title">ENV 2</div>
                                <div class="mod-controls" id="minimod_env2_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack output mod" data-jack="env2Out" title="Envelope 2">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                                <button class="mod-trigger-btn" id="minimod_env2_trigger">TRIG</button>
                            </div>
                        </div>

                        <!-- Row 3: Filter, VCAs, Mixer -->
                        <div class="minimod-row">
                            <!-- VCF -->
                            <div class="mod-module mod-vcf">
                                <div class="mod-title">VCF</div>
                                <div class="mod-controls" id="minimod_vcf_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input" data-jack="vcfIn" title="Audio Input">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">IN</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="vcfMod" title="Cutoff Mod">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">MOD</span>
                                    </div>
                                    <div class="mod-jack output" data-jack="vcfOut" title="Filter Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>

                            <!-- VCA 1 -->
                            <div class="mod-module mod-vca1">
                                <div class="mod-title">VCA 1</div>
                                <div class="mod-controls" id="minimod_vca1_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input" data-jack="vca1In" title="Audio Input">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">IN</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="vca1Mod" title="Gain Mod">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">MOD</span>
                                    </div>
                                    <div class="mod-jack output" data-jack="vca1Out" title="VCA Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>

                            <!-- VCA 2 -->
                            <div class="mod-module mod-vca2">
                                <div class="mod-title">VCA 2</div>
                                <div class="mod-controls" id="minimod_vca2_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input" data-jack="vca2In" title="Audio Input">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">IN</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="vca2Mod" title="Gain Mod">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">MOD</span>
                                    </div>
                                    <div class="mod-jack output" data-jack="vca2Out" title="VCA Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>

                            <!-- MIXER -->
                            <div class="mod-module mod-mix wide">
                                <div class="mod-title">MIXER</div>
                                <div class="mod-controls" id="minimod_mix_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input" data-jack="mix1In" title="Mix Input 1">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">1</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="mix2In" title="Mix Input 2">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">2</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="mix3In" title="Mix Input 3">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">3</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="mix4In" title="Mix Input 4">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">4</span>
                                    </div>
                                    <div class="mod-jack output" data-jack="mixOut" title="Mixer Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Row 4: Utilities -->
                        <div class="minimod-row">
                            <!-- MULT -->
                            <div class="mod-module mod-mult">
                                <div class="mod-title">MULT</div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input" data-jack="multIn" title="Signal Input">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">IN</span>
                                    </div>
                                    <div class="mod-jack output" data-jack="mult1Out" title="Copy 1">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">1</span>
                                    </div>
                                    <div class="mod-jack output" data-jack="mult2Out" title="Copy 2">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">2</span>
                                    </div>
                                    <div class="mod-jack output" data-jack="mult3Out" title="Copy 3">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">3</span>
                                    </div>
                                </div>
                            </div>

                            <!-- RING MOD -->
                            <div class="mod-module mod-ring">
                                <div class="mod-title">RING</div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input" data-jack="ringCarrierIn" title="Carrier Input">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">CAR</span>
                                    </div>
                                    <div class="mod-jack input" data-jack="ringModIn" title="Modulator Input">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">MOD</span>
                                    </div>
                                    <div class="mod-jack output" data-jack="ringOut" title="Ring Mod Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>

                            <!-- S&H -->
                            <div class="mod-module mod-sh">
                                <div class="mod-title">S&H</div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input" data-jack="shIn" title="Sample Input">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">IN</span>
                                    </div>
                                    <div class="mod-jack output mod" data-jack="shOut" title="Held Value">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                                <button class="mod-trigger-btn small" id="minimod_sh_trigger">S</button>
                            </div>

                            <!-- SLEW -->
                            <div class="mod-module mod-slew">
                                <div class="mod-title">SLEW</div>
                                <div class="mod-controls" id="minimod_slew_controls"></div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input" data-jack="slewIn" title="Input">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">IN</span>
                                    </div>
                                    <div class="mod-jack output mod" data-jack="slewOut" title="Slewed Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">OUT</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Row 5: Output -->
                        <div class="minimod-row">
                            <!-- OUTPUT -->
                            <div class="mod-module mod-output">
                                <div class="mod-title">OUTPUT</div>
                                <div class="mod-jacks">
                                    <div class="mod-jack input main-out" data-jack="mainOut" title="Main Output">
                                        <div class="jack-hole"></div>
                                        <span class="jack-label">IN</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="minimod-actions">
                            <button class="mod-btn" id="minimod_reset" title="Reset patching">Reset Patch</button>
                            <button class="mod-btn" id="minimod_clear" title="Clear all cables">Clear All</button>
                        </div>
                    </div>

                    <!-- 16-step Sequencer (like other instruments) -->
                    <div class="sequencer machine-sequencer">
                        <div class="step-markers">
                            ${Array.from({length: 16}, (_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                        </div>
                        <div class="seq-row">
                            <span class="seq-label">GATE</span>
                            <div class="steps" id="minimod_gates"></div>
                        </div>
                        <div class="seq-row">
                            <span class="seq-label">NOTE</span>
                            <div class="note-row" id="minimod_notes"></div>
                        </div>
                    </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createAllKnobs();
            this.setupJackInteraction();
            this.setupTriggerButtons();
            this.setupResetButtons();
            this.setupPresetSelector();
            this.createSequencer();

            requestAnimationFrame(() => {
                this.updateJackPositions();
                this.renderCables();
            });
        }
    }

    setupCollapseButton() {
        const btn = document.getElementById('minimod_collapse');
        if (!btn) return;

        btn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('minimod_power');
        const machine = document.getElementById('minimod_machine');

        powerBtn?.addEventListener('click', () => {
            const isOn = this.minimod.getPower();
            const newState = !isOn;
            this.minimod.setPower(newState);
            this.store.setPowerState('minimod', newState);
            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    createAllKnobs() {
        // VCO 1
        // Octave options for VCOs
        const octaveOptions = [
            { value: -2, label: "-2" },
            { value: -1, label: "-1" },
            { value: 0, label: "0" },
            { value: 1, label: "+1" },
            { value: 2, label: "+2" }
        ];

        const vco1Container = document.getElementById('minimod_vco1_controls');
        if (vco1Container) {
            this.components.selects.vco1Octave = themeManager.createSelect(vco1Container, {
                options: octaveOptions,
                value: this.minimod.params.vco1Octave,
                className: 'octave-select',
                label: 'Oct'
            }).onChange(v => this.minimod.setVco1Octave(parseInt(v)));

            this.components.knobs.vco1Detune = themeManager.createKnob(vco1Container, {
                min: -50, max: 50, value: this.minimod.params.vco1Detune,
                label: 'Det', size: 'small'
            }).onChange(v => this.minimod.setVco1Detune(v));
        }

        // VCO 2
        const vco2Container = document.getElementById('minimod_vco2_controls');
        if (vco2Container) {
            this.components.selects.vco2Octave = themeManager.createSelect(vco2Container, {
                options: octaveOptions,
                value: this.minimod.params.vco2Octave,
                className: 'octave-select',
                label: 'Oct'
            }).onChange(v => this.minimod.setVco2Octave(parseInt(v)));

            this.components.knobs.vco2Detune = themeManager.createKnob(vco2Container, {
                min: -50, max: 50, value: this.minimod.params.vco2Detune,
                label: 'Det', size: 'small'
            }).onChange(v => this.minimod.setVco2Detune(v));
        }

        // VCO 3
        const vco3Container = document.getElementById('minimod_vco3_controls');
        if (vco3Container) {
            this.components.selects.vco3Octave = themeManager.createSelect(vco3Container, {
                options: octaveOptions,
                value: this.minimod.params.vco3Octave,
                className: 'octave-select',
                label: 'Oct'
            }).onChange(v => this.minimod.setVco3Octave(parseInt(v)));

            this.components.knobs.vco3Detune = themeManager.createKnob(vco3Container, {
                min: -50, max: 50, value: this.minimod.params.vco3Detune,
                label: 'Det', size: 'small'
            }).onChange(v => this.minimod.setVco3Detune(v));
        }

        // LFO 1
        const lfo1Container = document.getElementById('minimod_lfo1_controls');
        if (lfo1Container) {
            this.components.knobs.lfo1Rate = themeManager.createKnob(lfo1Container, {
                min: 0.1, max: 20, value: this.minimod.lfo1Rate,
                label: 'Rate', step: 0.1, decimals: 1, size: 'small'
            }).onChange(v => this.minimod.setLfo1Rate(v));
        }

        // LFO 2
        const lfo2Container = document.getElementById('minimod_lfo2_controls');
        if (lfo2Container) {
            this.components.knobs.lfo2Rate = themeManager.createKnob(lfo2Container, {
                min: 0.1, max: 20, value: this.minimod.lfo2Rate,
                label: 'Rate', step: 0.1, decimals: 1, size: 'small'
            }).onChange(v => this.minimod.setLfo2Rate(v));
        }

        // ENV 1
        const env1Container = document.getElementById('minimod_env1_controls');
        if (env1Container) {
            this.components.knobs.env1A = themeManager.createKnob(env1Container, {
                min: 0.001, max: 2, value: this.minimod.params.env1Attack,
                label: 'A', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setEnv1Attack(v));

            this.components.knobs.env1D = themeManager.createKnob(env1Container, {
                min: 0.01, max: 2, value: this.minimod.params.env1Decay,
                label: 'D', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setEnv1Decay(v));

            this.components.knobs.env1S = themeManager.createKnob(env1Container, {
                min: 0, max: 1, value: this.minimod.params.env1Sustain,
                label: 'S', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setEnv1Sustain(v));

            this.components.knobs.env1R = themeManager.createKnob(env1Container, {
                min: 0.01, max: 3, value: this.minimod.params.env1Release,
                label: 'R', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setEnv1Release(v));
        }

        // ENV 2
        const env2Container = document.getElementById('minimod_env2_controls');
        if (env2Container) {
            this.components.knobs.env2A = themeManager.createKnob(env2Container, {
                min: 0.001, max: 2, value: this.minimod.params.env2Attack,
                label: 'A', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setEnv2Attack(v));

            this.components.knobs.env2D = themeManager.createKnob(env2Container, {
                min: 0.01, max: 2, value: this.minimod.params.env2Decay,
                label: 'D', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setEnv2Decay(v));

            this.components.knobs.env2S = themeManager.createKnob(env2Container, {
                min: 0, max: 1, value: this.minimod.params.env2Sustain,
                label: 'S', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setEnv2Sustain(v));

            this.components.knobs.env2R = themeManager.createKnob(env2Container, {
                min: 0.01, max: 3, value: this.minimod.params.env2Release,
                label: 'R', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setEnv2Release(v));
        }

        // VCF
        const vcfContainer = document.getElementById('minimod_vcf_controls');
        if (vcfContainer) {
            this.components.knobs.vcfCutoff = themeManager.createKnob(vcfContainer, {
                min: 50, max: 8000, value: this.minimod.vcfCutoff,
                label: 'Cutoff', unit: 'Hz', size: 'small'
            }).onChange(v => this.minimod.setVcfCutoff(v));

            this.components.knobs.vcfRes = themeManager.createKnob(vcfContainer, {
                min: 0, max: 20, value: this.minimod.vcfResonance,
                label: 'Res', size: 'small'
            }).onChange(v => this.minimod.setVcfResonance(v));
        }

        // VCA 1
        const vca1Container = document.getElementById('minimod_vca1_controls');
        if (vca1Container) {
            this.components.knobs.vca1Level = themeManager.createKnob(vca1Container, {
                min: 0, max: 1, value: this.minimod.params.vca1Level,
                label: 'Level', step: 0.01, size: 'small'
            }).onChange(v => this.minimod.setVca1Level(v));
        }

        // VCA 2
        const vca2Container = document.getElementById('minimod_vca2_controls');
        if (vca2Container) {
            this.components.knobs.vca2Level = themeManager.createKnob(vca2Container, {
                min: 0, max: 1, value: this.minimod.params.vca2Level,
                label: 'Level', step: 0.01, size: 'small'
            }).onChange(v => this.minimod.setVca2Level(v));
        }

        // MIXER
        const mixContainer = document.getElementById('minimod_mix_controls');
        if (mixContainer) {
            this.components.knobs.mix1 = themeManager.createKnob(mixContainer, {
                min: 0, max: 1, value: this.minimod.params.mix1Level,
                label: '1', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setMix1Level(v));

            this.components.knobs.mix2 = themeManager.createKnob(mixContainer, {
                min: 0, max: 1, value: this.minimod.params.mix2Level,
                label: '2', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setMix2Level(v));

            this.components.knobs.mix3 = themeManager.createKnob(mixContainer, {
                min: 0, max: 1, value: this.minimod.params.mix3Level,
                label: '3', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setMix3Level(v));

            this.components.knobs.mix4 = themeManager.createKnob(mixContainer, {
                min: 0, max: 1, value: this.minimod.params.mix4Level,
                label: '4', step: 0.01, size: 'tiny'
            }).onChange(v => this.minimod.setMix4Level(v));
        }

        // SLEW
        const slewContainer = document.getElementById('minimod_slew_controls');
        if (slewContainer) {
            this.components.knobs.slewTime = themeManager.createKnob(slewContainer, {
                min: 0.01, max: 1, value: this.minimod.params.slewTime,
                label: 'Time', step: 0.01, size: 'small'
            }).onChange(v => this.minimod.setSlewTime(v));
        }
    }

    setupJackInteraction() {
        const container = document.getElementById('minimod_content');
        const svg = document.getElementById('minimod_cables');
        const tempCable = document.getElementById('minimod_tempCable');

        if (!container || !svg) return;

        const jacks = container.querySelectorAll('.mod-jack');

        jacks.forEach(jack => {
            const jackId = jack.dataset.jack;
            const isOutput = jack.classList.contains('output');

            jack.addEventListener('mousedown', (e) => {
                e.preventDefault();
                const rect = jack.querySelector('.jack-hole').getBoundingClientRect();
                const svgRect = svg.getBoundingClientRect();

                this.dragging = {
                    jackId,
                    isOutput,
                    startX: rect.left + rect.width / 2 - svgRect.left,
                    startY: rect.top + rect.height / 2 - svgRect.top
                };

                tempCable.style.display = 'block';
            });

            jack.addEventListener('mouseenter', () => {
                if (this.dragging) {
                    const canConnect = this.dragging.isOutput !== jack.classList.contains('output');
                    jack.classList.toggle('valid-target', canConnect);
                    jack.classList.toggle('invalid-target', !canConnect);
                }
            });

            jack.addEventListener('mouseleave', () => {
                jack.classList.remove('valid-target', 'invalid-target');
            });
        });

        this._boundMouseMove = this._onMouseMove.bind(this);
        this._boundMouseUp = this._onMouseUp.bind(this);
        document.addEventListener('mousemove', this._boundMouseMove);
        document.addEventListener('mouseup', this._boundMouseUp);
    }

    _onMouseMove(e) {
        if (!this.dragging) return;

        const svg = document.getElementById('minimod_cables');
        const tempCable = document.getElementById('minimod_tempCable');
        if (!svg || !tempCable) return;

        const svgRect = svg.getBoundingClientRect();
        const endX = e.clientX - svgRect.left;
        const endY = e.clientY - svgRect.top;

        const path = this._createCablePath(
            this.dragging.startX, this.dragging.startY,
            endX, endY
        );
        tempCable.setAttribute('d', path);
    }

    _onMouseUp(e) {
        if (!this.dragging) return;

        const tempCable = document.getElementById('minimod_tempCable');
        if (tempCable) {
            tempCable.style.display = 'none';
            tempCable.setAttribute('d', '');
        }

        const target = document.elementFromPoint(e.clientX, e.clientY);
        const jackEl = target?.closest('.mod-jack');

        if (jackEl) {
            const targetJackId = jackEl.dataset.jack;
            const targetIsOutput = jackEl.classList.contains('output');

            if (this.dragging.isOutput && !targetIsOutput) {
                this.minimod.connect(this.dragging.jackId, targetJackId);
            } else if (!this.dragging.isOutput && targetIsOutput) {
                this.minimod.connect(targetJackId, this.dragging.jackId);
            }

            this.renderCables();
        }

        document.querySelectorAll('.mod-jack').forEach(j => {
            j.classList.remove('valid-target', 'invalid-target');
        });

        this.dragging = null;
    }

    updateJackPositions() {
        const svg = document.getElementById('minimod_cables');
        if (!svg) return;

        const svgRect = svg.getBoundingClientRect();
        const jacks = document.querySelectorAll('.mod-jack');

        jacks.forEach(jack => {
            const jackId = jack.dataset.jack;
            const hole = jack.querySelector('.jack-hole');
            if (!hole) return;

            const rect = hole.getBoundingClientRect();
            this.jackPositions[jackId] = {
                x: rect.left + rect.width / 2 - svgRect.left,
                y: rect.top + rect.height / 2 - svgRect.top
            };
        });
    }

    renderCables() {
        const cableGroup = document.getElementById('minimod_cableGroup');
        if (!cableGroup) return;

        this.updateJackPositions();
        cableGroup.innerHTML = '';

        const connections = this.minimod.getConnections();
        connections.forEach((conn) => {
            const start = this.jackPositions[conn.source];
            const end = this.jackPositions[conn.dest];

            if (!start || !end) return;

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', this._createCablePath(start.x, start.y, end.x, end.y));
            path.setAttribute('fill', 'none');
            path.setAttribute('stroke', this._getCableColor(conn.source));
            path.setAttribute('stroke-width', '4');
            path.setAttribute('stroke-linecap', 'round');
            path.setAttribute('filter', 'url(#cableGlow)');
            path.classList.add('patch-cable');
            path.dataset.source = conn.source;
            path.dataset.dest = conn.dest;

            path.addEventListener('dblclick', () => {
                this.minimod.disconnect(conn.source, conn.dest);
                this.renderCables();
            });

            cableGroup.appendChild(path);
        });
    }

    _createCablePath(x1, y1, x2, y2) {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const sag = Math.min(dist * 0.3, 60);

        const cx1 = x1 + dx * 0.25;
        const cy1 = y1 + sag;
        const cx2 = x1 + dx * 0.75;
        const cy2 = y2 + sag;

        return `M ${x1} ${y1} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x2} ${y2}`;
    }

    _getCableColor(sourceJackId) {
        if (sourceJackId.includes('lfo') || sourceJackId.includes('env') ||
            sourceJackId.includes('sh') || sourceJackId.includes('slew') ||
            sourceJackId.includes('seqCv')) {
            return 'url(#cableGradientMod)';
        }
        if (sourceJackId.includes('clock') || sourceJackId.includes('Gate')) {
            return 'url(#cableGradientGate)';
        }
        return 'url(#cableGradientAudio)';
    }

    setupTriggerButtons() {
        // ENV 1 trigger
        const env1Btn = document.getElementById('minimod_env1_trigger');
        if (env1Btn) {
            env1Btn.addEventListener('mousedown', () => {
                this.minimod.env1Attack();
                env1Btn.classList.add('active');
            });
            env1Btn.addEventListener('mouseup', () => {
                this.minimod.env1Release();
                env1Btn.classList.remove('active');
            });
            env1Btn.addEventListener('mouseleave', () => {
                this.minimod.env1Release();
                env1Btn.classList.remove('active');
            });
        }

        // ENV 2 trigger
        const env2Btn = document.getElementById('minimod_env2_trigger');
        if (env2Btn) {
            env2Btn.addEventListener('mousedown', () => {
                this.minimod.env2Attack();
                env2Btn.classList.add('active');
            });
            env2Btn.addEventListener('mouseup', () => {
                this.minimod.env2Release();
                env2Btn.classList.remove('active');
            });
            env2Btn.addEventListener('mouseleave', () => {
                this.minimod.env2Release();
                env2Btn.classList.remove('active');
            });
        }

        // S&H trigger
        const shBtn = document.getElementById('minimod_sh_trigger');
        if (shBtn) {
            shBtn.addEventListener('click', () => {
                this.minimod.sampleAndHold();
                shBtn.classList.add('active');
                setTimeout(() => shBtn.classList.remove('active'), 100);
            });
        }
    }

    setupResetButtons() {
        const resetBtn = document.getElementById('minimod_reset');
        if (resetBtn) {
            resetBtn.addEventListener('click', () => {
                this.minimod.resetPatching();
                this.renderCables();
            });
        }

        const clearBtn = document.getElementById('minimod_clear');
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                this.minimod.disconnectAll();
                this.renderCables();
            });
        }
    }

    setupPresetSelector() {
        const select = document.getElementById('minimod_preset');
        if (!select) return;

        select.addEventListener('change', () => {
            const presetName = select.value;
            const preset = MINIMOD_PRESETS[presetName];
            if (preset) {
                this.minimod.loadPreset(preset);
                this.renderCables();
                this.updateKnobsFromParams();
            }
        });
    }

    updateKnobsFromParams() {
        const p = this.minimod.params;

        // Update octave selects
        this.components.selects.vco1Octave?.setValue(p.vco1Octave);
        this.components.selects.vco2Octave?.setValue(p.vco2Octave);
        this.components.selects.vco3Octave?.setValue(p.vco3Octave);

        // Update knob displays
        this.components.knobs.vco1Detune?.setValue(p.vco1Detune);
        this.components.knobs.vco2Detune?.setValue(p.vco2Detune);
        this.components.knobs.vco3Detune?.setValue(p.vco3Detune);
        this.components.knobs.lfo1Rate?.setValue(p.lfo1Rate);
        this.components.knobs.lfo2Rate?.setValue(p.lfo2Rate);
        this.components.knobs.vcfCutoff?.setValue(p.vcfCutoff);
        this.components.knobs.vcfRes?.setValue(p.vcfResonance);
        this.components.knobs.mix1?.setValue(p.mix1Level);
        this.components.knobs.mix2?.setValue(p.mix2Level);
        this.components.knobs.mix3?.setValue(p.mix3Level);
        this.components.knobs.mix4?.setValue(p.mix4Level);
    }

    /**
     * Crée le séquenceur 16 steps (comme les autres instruments)
     */
    createSequencer() {
        const notesContainer = document.getElementById('minimod_notes');
        const gatesContainer = document.getElementById('minimod_gates');

        if (!notesContainer || !gatesContainer) return;

        // Note selectors
        for (let i = 0; i < 16; i++) {
            this.components.selects[`note_${i}`] = themeManager.createSelect(notesContainer, {
                options: this.store.noteOptions,
                value: this.store.patterns.minimodNotes ? this.store.patterns.minimodNotes[i] : 'C3',
                className: 'note-select'
            }).onChange(value => {
                this.store.setStep('minimodNotes', i, value);
            });
        }

        // Gate buttons
        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(gatesContainer, {
                index: i,
                type: 'gate',
                active: this.store.patterns.minimodGates ? this.store.patterns.minimodGates[i] : false
            }).onChange(active => {
                this.store.setStep('minimodGates', i, active);
            });

            this.components.stepButtons.push(stepBtn);
        }
    }

    destroy() {
        Object.values(this.components.knobs).forEach(k => k?.destroy?.());
        Object.values(this.components.selects).forEach(s => s?.destroy?.());
        this.components.stepButtons.forEach(b => b?.destroy?.());
        if (this._boundMouseMove) {
            document.removeEventListener('mousemove', this._boundMouseMove);
        }
        if (this._boundMouseUp) {
            document.removeEventListener('mouseup', this._boundMouseUp);
        }
    }
}
